import { useCallback, useEffect, useState } from "react";
import type { ZodType, ZodTypeDef } from "zod";

import { isRelevantMutation } from "./_is-relevant-mutation";

export interface GetCssVar {
	(name: `--${string}`): string | undefined;
	/* The schema receives the raw CSS variable string. Use `z.coerce.*` when parsing non-string types (e.g. `z.coerce.number()`). */
	<T>(name: `--${string}`, schema: ZodType<T, ZodTypeDef, unknown>): T | undefined;
}

/**
 * Reactively reads CSS custom properties from a DOM element on demand.
 *
 * A `MutationObserver` on `document.documentElement` watches `style`, `class`,
 * and `data-mode` attribute changes. Only mutations on the target or its
 * ancestors trigger a re-read, and only actual value changes cause a re-render.
 */
export function useCssVars<T extends Element = Element>(): [React.Dispatch<T>, GetCssVar] {
	const [element, setElement] = useState<null | T>(null);
	const [trackedVariables, setTrackedVariables] = useState<Map<string, string>>(() => new Map());

	// getCssVar — lazy accessor, new identity when snapshot changes.
	const getCssVar = useCallback(
		(name: `--${string}`, schema?: ZodType<unknown, ZodTypeDef, unknown>) => {
			// biome-ignore lint/style/noRestrictedGlobals: encapsulation
			if (element != null && !trackedVariables.has(name) && typeof window !== "undefined") {
				// biome-ignore lint/style/noRestrictedGlobals: encapsulation
				const computed = window.getComputedStyle(element);
				const value = computed.getPropertyValue(name).trim();
				trackedVariables.set(name, value);
			}

			const raw = trackedVariables.get(name) || undefined;

			if (raw == null || schema == null) {
				return raw;
			}

			const result = schema.safeParse(raw);
			return result.success ? result.data : undefined;
		},
		[trackedVariables, element],
	) as GetCssVar;

	useEffect(() => {
		if (element == null) {
			return;
		}

		// biome-ignore lint/style/noRestrictedGlobals: encapsulation
		const computed = window.getComputedStyle(element);

		const flush = () => {
			for (const name of trackedVariables.keys()) {
				const value = computed.getPropertyValue(name).trim();
				if (trackedVariables.get(name) !== value) {
					setTrackedVariables(new Map());
					break;
				}
			}
		};

		flush();

		const observer = new MutationObserver((mutations) => {
			if (isRelevantMutation(mutations, element)) {
				flush();
			}
		});

		observer.observe(document.documentElement, {
			subtree: true,
			attributes: true,
			attributeFilter: ["style", "class", "data-mode"],
		});

		return () => {
			observer.disconnect();
		};
	}, [trackedVariables, element]);

	return [setElement, getCssVar];
}
